import { useCallback } from 'react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { useAuth } from '../auth/AuthContext';

/** Sign THIS device out.
 *
 *  Before dropping the token we unsubscribe this browser from Web Push and delete its
 *  server row, so the person signing out stops receiving the account's call
 *  notifications. Otherwise, on a shared device, the *previous* user keeps getting
 *  them until someone else logs in and re-subscribes (the server upserts by endpoint).
 *
 *  Order matters: the `pushUnsubscribe` request runs while the token is still set (it
 *  needs the auth header), and `setToken(null)` only fires afterwards. It is all
 *  best-effort — a push teardown failure (offline, no worker, insecure context) must
 *  never block the sign-out, so the token is cleared in `finally`. */
export function useLogout() {
  const { setToken } = useAuth();
  const crud = useClientQueries(schema);
  const unsubscribe = crud.$procs.pushUnsubscribe.useMutation();

  return useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker?.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await unsubscribe.mutateAsync({ args: { input: { endpoint: sub.endpoint } } });
        await sub.unsubscribe();
      }
    } catch {
      // Ignore — signing out must not depend on push teardown.
    } finally {
      setToken(null);
    }
  }, [setToken, unsubscribe]);
}
